<template>
  <td ref="cellRef" :class="{showingPopover, isCellSelected}" :style="{background: cellColor}" @click="togglePopover()" v-bind="$attrs">
    <div ref="popoverAnchor" class="popoverAnchorMarker"></div>
    <div class="cellDiv" :style="{ 'visibility' : isCellVisible ? 'visible' : 'hidden' }">
      <OptionalSettingCellContent :tag>
        <slot></slot>
      </OptionalSettingCellContent>
    </div>
  </td>
  <slot name="OverlayOverride" :helpText>
    <template v-if="showingDesktopUi">
      <Popover ref="popover" class="tableCell-popover" v-model:showingPopover="showingPopover" :popoverAnchor="popoverAnchor" :popoverPadding="popoverPadding">
        <OptionalSettingPopoverContent
          :tag
          :popoverTitle
          :selectedCells
          v-model:showingPopover="showingPopover"
          v-model:columnSelectionCode="columnSelectionCode"
        >
          <slot name="PopoverContentDesktop" :helpText></slot>
          <template #BinSelectionInput>
            <slot name="BinSelectionInput"></slot>
          </template>
        </OptionalSettingPopoverContent>
      </Popover>
    </template>
    <template v-else>
      <slot name="DialogOverrideMobile">
        <Dialog modal dismissableMask v-model:visible="showingPopover" :closable="false" :draggable="false" :showHeader="false"
          :contentStyle="{ overflow: popoverScroll ? 'auto' : 'visible' }"
        >
          <OptionalSettingPopoverContent
            :tag
            :popoverTitle
            :selectedCells
            v-model:showingPopover="showingPopover"
            v-model:columnSelectionCode="columnSelectionCode"
          >
            <template v-if="$slots.DialogContentMobile">
              <slot name="DialogContentMobile" :helpText></slot>
            </template>
            <template v-else>
              <slot name="PopoverContentDesktop" :helpText></slot>
            </template>
          </OptionalSettingPopoverContent>
        </Dialog>
      </slot>
    </template>
  </slot>
</template>

<script setup>
import { computed, ref, watch, watchEffect } from "vue";
import { showingDesktopUi } from "./store.js";
import Popover from "./Popover.vue";
import OptionalSettingCellContent from "./OptionalSettingCellContent.vue";
import OptionalSettingPopoverContent from "./OptionalSettingPopoverContent.vue";

let props = defineProps({
  tag: {},
  cellColor: {},
  showingPopover: {},
  popoverDisabled: {},
  popoverTitle: {},
  popoverScroll: { type: Boolean, default: true },
  popoverPadding: {},
  columnSelectionCode: {},
  selectedCells: {},
  helpText: {}
});

let showingPopover = defineModel("showingPopover");
let columnSelectionCode = defineModel("columnSelectionCode");

let isCellVisible = computed(() => {
  return props.tag.committed && !props.tag.$session.timedOut;
});
watch(isCellVisible, () => {
  showingPopover.value = false;
});

let isCellSelected = computed(() => {
  return props.selectedCells?.map(cell => cell.componentArgs.tag).includes(props.tag);
});

let popover = ref(null);
let cellRef = ref(null);
let popoverAnchor = ref(null);

function togglePopover() {
  if (isCellVisible.value && !props.popoverDisabled)
    showingPopover.value = !showingPopover.value;
}

let polledHelpText = ref();
let helpText = computed(() => {
  return (props.helpText != "") ? props.helpText : polledHelpText.value;
});

watch(showingPopover, () => {
  let tag = props.tag;
  let session = tag.$session;
  if (showingPopover.value && props.helpText == "") {
    let tagHandle = parseInt(tag.handle); // Parse removes any -clone suffix if present on the handle.
    session.startPollingFor("helpText", { tagHandle }, result => {
      polledHelpText.value = result;
    });
  }
  else {
    session.stopPollingFor("helpText");
    polledHelpText.value = null;
  }
});

watch(helpText, () => {
  if (showingPopover.value)
    repositionPopover();
});

async function repositionPopover() {
  if (showingDesktopUi.value) {
    await popover.value.reposition();
  }
}

watch(() => store.showAdvanced, async () => {
  if (showingPopover.value)
    await repositionPopover();
});

defineExpose({
  repositionPopover
});

</script>

<style>
.tableCell-popover {
  box-shadow: 
    0 4px 6px -1px rgba(0, 0, 0, 0.1), 
    0 2px 4px -2px rgba(0, 0, 0, 1)
}
.tableCell-popover .p-overlaypanel-content {
  padding: 20px;
}

td {
  position: relative;
}

.popoverAnchorMarker {
  position: absolute;
  left: calc(50% + var(--spanning-cell-visible-center-offset, 0px));
  top: 50%;
  width: 0;
  height: 0;
  pointer-events: none;
  transform: translate(-50%, -50%);
}
</style>